// @flow
import React, { type Node, useMemo } from 'react'
import path from 'ramda/es/path'

import {
  RowLayoutWithSticky,
  RowLayoutContent,
  Header,
  Header2,
} from 'app/common/layouts/RowLayout'
import { OptionProp, OptionProps } from 'app/common/ui-next/option-props'
import { useVendingMachineSingle, useRelatedOrgs } from 'app/dataProviders/VendingDataProvider'
import PlainIcon, { iconTypes } from 'app/common/ui-next/plain-icon'
import useBuildingsData from 'app/components/common/BuildingsData/useBuildingsData'
import { useSalePoints } from 'app/dataProviders/salePoint/hooks/useSalePoints'
import { vendingMachineKindItems } from 'app/dataTypes/vending/MachineKind.types'

import { MachineHistory } from '../MachineHistory'
import EditMachineButton from './editMachine/EditMachineButton'
import useModelById from '../useModelById'
import useVersionById from '../useVersionById'
import styles from './GeneralSection.scss'

type Props = {
  header: string,
  loading: boolean,
  onDeleteSuccess: () => void,
}

const GeneralSection = ({ header, loading, onDeleteSuccess }: Props): Node => {
  const [{ id: machineId, title, number, orgId, buildingId, salePointId, modelId, versionId, kind }] =
    useVendingMachineSingle()
  const relatedOrgs = useRelatedOrgs()
  const model = useModelById(modelId)
  const version = useVersionById(versionId)
  const [buildings, loadingBuildings] = useBuildingsData(orgId, Boolean(orgId))

  const salePointsParams = useMemo(() => {
    const params = {
      orgId: String(orgId),
    }
    if (buildingId) {
      params.buildingId = String(buildingId)
    }
    return params
  }, [orgId, buildingId])

  const { data: salePoints, loading: loadingSalePoints } = useSalePoints({
    params: salePointsParams,
    autoload: Boolean(orgId),
    autoupdate: Boolean(orgId),
    initialLoading: false,
  })

  const relatedOrgTitle = path([orgId, 'title'], relatedOrgs)
  const buildingTitle = path([buildingId, 'title'], buildings)
  const salePoint = salePoints.find(({ id }) => String(id) === String(salePointId))
  const kindItem = vendingMachineKindItems.find(({ id }) => id === kind)

  return (
    <RowLayoutWithSticky
      number={3}
      content={
        <RowLayoutContent borderBottom className={styles.topbar}>
          <div className={styles.topbarHeader}>
            <Header>{header}</Header>
            <Header2>{`Производитель: ${model || '...'} ${version || ''}`}</Header2>
            <MachineHistory key={machineId} machineId={machineId} title={title} />
          </div>
          <EditMachineButton loading={loading} onDeleteSuccess={onDeleteSuccess} />
        </RowLayoutContent>
      }
    >
      <RowLayoutContent className={styles.props}>
        <OptionProps>
          <OptionProp title="Наименование аппарата" loading={loading}>
            {title}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Номер аппарата" loading={loading}>
            {number}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Модель аппарата" loading={loading}>
            {version}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Вид аппарата" loading={loading}>
            {path(['title'], kindItem)}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Ошибки" loading={loading}>
            <PlainIcon type={iconTypes.OK} />
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Количество товара" loading={loading}>
            {'100%'}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Последняя загрузка" loading={loading}>
            {'—'}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Последняя покупка" loading={loading}>
            {'—'}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Место размещения" loading={loading || !relatedOrgTitle}>
            {relatedOrgTitle}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Корпус" loading={loading || loadingBuildings || !buildingTitle}>
            {buildingTitle}
          </OptionProp>
        </OptionProps>
        <OptionProps>
          <OptionProp title="Точка продаж" loading={loading || loadingSalePoints || !salePoint}>
            {path(['title'], salePoint)}
          </OptionProp>
        </OptionProps>
      </RowLayoutContent>
    </RowLayoutWithSticky>
  )
}

export default GeneralSection
